import React from "react"
import { Formik, FormikProps } from "formik"
import { Mutation } from "react-apollo"
import { Input, Button, Form, Divider, Flex, Checkbox, Text } from "@jjordy/layout"
import { Link } from "@reach/router"
import Center from "../Elements/Center"
import gql from "graphql-tag"

const REGISTER_ADMIN_MUTATION = gql`
  mutation RegisterAdmin($input: RegisterAdminInput!) {
    registerAdmin(input: $input) {
      person {
        id
      }
    }
  }
`

const getErrorFromCode = (code: string) => {
  const codes = {
    ["23505"]: "Someone has already signed up for an account with this address."
  }
  return codes[code] || null
}

export interface RegisterAdminFormProps {
  onDone: (values: any) => void
  tenantId: string
}

export interface RegisterAdminData {
  data?: {
    registerAdmin: {
      person: {
        id: string
      }
    }
  }
  loading?: boolean
  error?: Error
  [key: string]: any
}

export interface RegisterAdminVariables {
  input: {
    firstName: string
    lastName: string
    email: string
    password: string
    agreetos: boolean
  }
}

interface FormValues {
  firstName: string
  lastName: string
  email: string
  password: string
  agreetos: boolean
}

interface ErrorValues {
  firstName?: string
  lastName?: string
  email?: string
  password?: string
  agreetos?: string
}

class RegisterAdminMutation extends Mutation<RegisterAdminData, RegisterAdminVariables> {}

const RegisterAdminForm: React.SFC<RegisterAdminFormProps> = ({ tenantId, onDone }) => (
  <RegisterAdminMutation mutation={REGISTER_ADMIN_MUTATION}>
    {(registerAdmin, { loading }) => (
      <Formik
        onSubmit={(values: FormValues, actions: FormikProps<FormValues>) => {
          actions.setSubmitting(true)
          registerAdmin({
            variables: {
              input: values
            }
          })
            .then((data) => {
              actions.setSubmitting(false)
              onDone(data)
            })
            .catch((err) => {
              err.graphQLErrors.map((e: any) => {
                const first = e.detail.split("(")[1]
                const key = first.split(")")[0]
                actions.setFieldError(key, getErrorFromCode(e.errcode))
              })
            })
        }}
        validate={(values: FormValues) => {
          const errors: ErrorValues = {}
          if (!values.firstName) {
            errors.firstName = "First name required."
          }
          if (!values.lastName) {
            errors.lastName = "Last name required."
          }
          if (!values.email) {
            errors.email = "Email address required."
          }
          if (!values.password) {
            errors.password = "Password required."
          }
          if (!values.agreetos) {
            errors.agreetos = "Please agree to the terms and conditions."
          }
          return errors
        }}
        initialValues={{ firstName: "", lastName: "", email: "", password: "", tenantid: tenantId, agreetos: false }}
        render={({ handleSubmit, handleChange, values, errors, touched, setFieldValue }) => (
          <Form onSubmit={handleSubmit} loading={loading}>
            <Flex>
              <Input
                name="firstName"
                label="First Name"
                type="text"
                onChange={handleChange}
                error={errors.firstName}
                message={errors.firstName}
                value={values.firstName}
                mr={1}
              />
              <Input
                name="lastName"
                label="Last Name"
                type="text"
                onChange={handleChange}
                error={errors.lastName}
                message={errors.lastName}
                value={values.lastName}
              />
            </Flex>
            <Input
              name="email"
              label="Email"
              type="email"
              onChange={handleChange}
              error={errors.email}
              message={errors.email}
              value={values.email}
            />
            <Input
              name="password"
              type="password"
              label="Password"
              onChange={handleChange}
              value={values.password}
              error={errors.password}
            />
            <Center>
              <Checkbox
                label={
                  <Text fg="grey" small m={0}>
                    I Agree to the <Link to="/tos">Terms and Conditions</Link>
                  </Text>
                }
                onChange={(v: boolean) => setFieldValue("agreetos", v)}
              />
            </Center>
            {errors.agreetos && (
              <Text m={0} small fg="red">
                {errors.agreetos}
              </Text>
            )}
            <Divider />
            <Button fluid type="submit" my={1} bg="success">
              REGISTER ACCOUNT
            </Button>
          </Form>
        )}
      />
    )}
  </RegisterAdminMutation>
)

export default RegisterAdminForm
